import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Financial number formatting utilities
export function formatCurrency(
  amount: number,
  options?: {
    compact?: boolean;
    precision?: number;
    showCents?: boolean;
  }
): string {
  const { compact = true, precision = 1, showCents = false } = options || {};

  if (compact) {
    // For very large numbers, use compact notation
    if (Math.abs(amount) >= 1000000000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
        compactDisplay: "short",
      }).format(amount);
    }

    // For millions
    if (Math.abs(amount) >= 1000000) {
      const millions = amount / 1000000;
      const formatted = millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(precision);
      return `$${formatted}M`;
    }

    // For thousands
    if (Math.abs(amount) >= 1000) {
      const thousands = amount / 1000;
      const formatted = thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(precision);
      return `$${formatted}K`;
    }
  }

  // For smaller amounts or when compact is false
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(amount);
}

export function formatNumber(
  num: number,
  options?: {
    compact?: boolean;
    precision?: number;
  }
): string {
  const { compact = true, precision = 1 } = options || {};

  if (!compact) {
    return num.toLocaleString("en-US");
  }

  // For billions
  if (Math.abs(num) >= 1000000000) {
    const billions = num / 1000000000;
    const formatted = billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(precision);
    return `${formatted}B`;
  }

  // For millions
  if (Math.abs(num) >= 1000000) {
    const millions = num / 1000000;
    const formatted = millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(precision);
    return `${formatted}M`;
  }

  // For thousands
  if (Math.abs(num) >= 1000) {
    const thousands = num / 1000;
    const formatted = thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(precision);
    return `${formatted}K`;
  }

  return num.toString();
}

export function formatPercentage(
  value: number,
  options?: {
    precision?: number;
    showSign?: boolean;
  }
): string {
  const { precision = 1, showSign = false } = options || {};

  const formatted = value.toFixed(precision);
  const sign = showSign && value > 0 ? "+" : "";

  return `${sign}${formatted}%`;
}

export function formatMetric(
  value: number,
  type: "currency" | "number" | "percentage",
  options?: {
    compact?: boolean;
    precision?: number;
    showSign?: boolean;
    showCents?: boolean;
  }
): string {
  switch (type) {
    case "currency":
      return formatCurrency(value, options);
    case "number":
      return formatNumber(value, options);
    case "percentage":
      return formatPercentage(value, options);
    default:
      return value.toString();
  }
}

// Utility to determine the best format for a financial metric
export function smartFormatCurrency(amount: number): string {
  // Always use compact format for amounts over 1000
  if (Math.abs(amount) >= 1000) {
    return formatCurrency(amount, { compact: true });
  }
  // Show cents for smaller amounts
  return formatCurrency(amount, { compact: false, showCents: true });
}

// Utility for displaying budget usage ratios
export function formatBudgetRatio(spent: number, allocated: number): string {
  const spentFormatted = formatCurrency(spent);
  const allocatedFormatted = formatCurrency(allocated);
  const percentage = ((spent / allocated) * 100).toFixed(0);

  return `${spentFormatted} / ${allocatedFormatted} (${percentage}%)`;
}
